import moment from 'moment-timezone';
import { Activity } from './activity';
import { Category } from './accounting/category';
import { DepositBankAccount } from './deposit-bank-account';
import { LedgerEntry } from './ledger-entry';
import { Membership } from './membership';
import { Organization } from './organization';
import { PaymentItem } from './payment-item';
import { User } from './user';
import { ChargeLateFee } from './charge-late-fee';
import { Unit } from './unit';
import { Note } from './note';

export class Charge
{
    id: number;
    organizationId: number;
    parentId: number;
    payorId: number;
    payorType: string;
    depositBankAccountId: number;
    categoryId: number;
    title: string;
    description: string;
    emailAppendMessage: string;
    quantity: number;
    currency: string;
    chargeAmount: number;
    paymentsSum: number;
    balance: number;
    paid: boolean;
    forgiven: boolean;
    activeAfter: string;
    paymentDueOn: string;
    earlyBefore: string;
    earlyDiscount: number;
    lateAfter: string;
    lateFee: number;
    expectedTotal: number = 0;
    openingBalance: boolean = false;
    createdAt: string;
    updatedAt: string;
    deletedAt: string;

    lastActivity: Activity;
    category: Category;
    depositBankAccount: DepositBankAccount;
    organization: Organization;
    payor: Membership | Organization | User | Unit;
    payments: PaymentItem;

    displayName: string;
    familyName: string;
    givenNames: string;
    accountDisplayName: string;
    name: string;

    ownerId: number;
    ownerType: string;
    paymentAmount: number;

    reason: string;
    // When true, Charge is meant to be added to DOM but not rendered.
    hidden: boolean;
    checked: boolean;
    showDetails: boolean;
    autopayEnabled: boolean;

    removeLateFees: boolean;
    removeEarlyDiscounts: boolean;
    emailInvoice: boolean;
    lateFees: ChargeLateFee[] = [];
    ledgerEntries: LedgerEntry[] = [];
    children: Charge[] = [];
    childrenCount = 0;
    notes: Note[] = [];
    history: {
        paymentItemId?: number
        paymentMethodType?: string,
        activityType: string,
        activityDate: string,
        charge: number,
        payment: number,
        balance: number
    }[] = [];

    constructor(charge: any) {
        if (charge.id) {
            this.id = charge.id;
        }
        if (charge.children) {
            this.children = charge.children.map(child => new Charge(child));
        }
        if (charge.parentId) {
            this.parentId = charge.parentId;
        }
        if (charge.expectedTotal) {
            this.expectedTotal = charge.expectedTotal;
        }
        if (charge.organizationId) {
            this.organizationId = charge.organizationId;
        }
        if (charge.payorId) {
            this.payorId = charge.payorId;
        }
        if (charge.payorType) {
            this.payorType = charge.payorType;
        }
        if (charge.name) {
            this.name = charge.name;
        }
        if (charge.depositBankAccountId) {
            this.depositBankAccountId = charge.depositBankAccountId;
        }
        if (charge.categoryId) {
            this.categoryId = charge.categoryId;
        }
        if (charge.title) {
            this.title = charge.title;
        }
        if (charge.description) {
            this.description = charge.description;
        }
        if (charge.emailAppendMessage) {
            this.emailAppendMessage = charge.emailAppendMessage;
        }
        if (charge.quantity) {
            this.quantity = charge.quantity;
        }
        if (charge.currency) {
            this.currency = charge.currency;
            this.currency = this.currency.toLocaleUpperCase();
        }
        this.openingBalance = charge.openingBalance ?? false;
        this.chargeAmount = charge.chargeAmount || 0;
        this.paymentsSum = charge.paymentsSum || 0;
        this.balance = charge.balance || 0;
        if (charge.paid) {
            this.paid = charge.paid;
        } else {
            this.paid = false;
        }
        this.forgiven = charge.forgiven ?? false;
        if (charge.earlyDiscount) {
            this.earlyDiscount = charge.earlyDiscount;
        }
        if (charge.lateFee) {
            this.lateFee = charge.lateFee;
        }

        this.activeAfter = charge.activeAfter;
        this.paymentDueOn = charge.paymentDueOn;
        this.earlyBefore = charge.earlyBefore;
        this.lateAfter = charge.lateAfter;
        this.createdAt = charge.createdAt;
        this.updatedAt = charge.updatedAt;
        this.deletedAt = charge.deletedAt;

        if (charge.history) {
            this.history = charge.history;
        }

        if (charge.lastActivity) {
            this.lastActivity = new Activity(charge.lastActivity);
        }
        if (charge.category) {
            this.category = new Category(charge.category);
        }
        if (charge.accountDisplayName) {
            this.accountDisplayName = charge.accountDisplayName;
        }
        if (charge.depositBankAccount) {
            this.depositBankAccount = new DepositBankAccount(charge.depositBankAccount);
        }
        if (charge.organization) {
            this.organization = new Organization(charge.organization);
        }
        if (charge.payor && charge.payorType !== null) {
            if (charge.payorType.indexOf('Membership') !== -1) {
                this.payor = new Membership(charge.payor);
            } else if (charge.payorType.indexOf('Organization') !== -1) {
                this.payor = new Organization(charge.payor);
            } else if (charge.payorType.indexOf('User') !== -1) {
                this.payor = new User(charge.payor);
            } else if (charge.payorType.indexOf('Unit') !== -1) {
                this.payor = new Unit(charge.payor);
            }
        }
        if (charge.payments) {
            this.payments = charge.payments.map((paymentItem) => {
                return new PaymentItem(paymentItem);
            });
        }
        if (charge.childrenCount) {
            this.childrenCount = charge.childrenCount;
        }

        this.autopayEnabled = charge.autopayEnabled ?? false;

        if (charge.hasOwnProperty('emailInvoice')) {
            this.emailInvoice = charge.emailInvoice;
        }
        if (charge.lateFees) {
            this.lateFees = charge.lateFees.map((fee) => {
                return new ChargeLateFee(fee);
            });
        }
        if (charge.notes) {
            this.notes = charge.notes.map(note => new Note(note));
        }
        if (charge.ledgerEntries) {
            this.ledgerEntries = charge.ledgerEntries.map(entry => new LedgerEntry(entry));
        }
    }
}
